import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import Draggable16 from '@carbon/icons-react/es/draggable/16';
import dotPropImmutable from 'dot-prop-immutable';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ProjectId } from 'api/models';
import { Space, SpaceTab } from 'api/space';
import { IconShell } from 'components/atoms/icon-shell';
import { toastService } from 'components/molecules/toast';
import { PermissionRuleName } from 'constant';
import { insert } from 'data/templates/projects/utils';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslation } from 'hooks/use-translation';
import { useProjectSpacesQuery } from 'queries';
import { logger } from 'services';
import { Tree } from './components';
import { toHashMap } from './util';

enum RearrangeSpaceNodes {
  SpaceTypeNode = 'SPACE-TYPE',
  SpaceNode = 'SPACE',
  SpaceTabNode = 'SPACE-TAB',
}

interface SpaceOrder {
  PrivateSpace: Array<Space>;
  SharedSpace: Array<Space>;
}

interface SpaceTypeNode {
  type: RearrangeSpaceNodes.SpaceTypeNode;
}

interface SpaceNode extends Space {
  type: RearrangeSpaceNodes.SpaceNode;
}

interface SpaceTabNode extends SpaceTab {
  type: RearrangeSpaceNodes.SpaceTabNode;
}

type SpaceData = SpaceTypeNode | SpaceNode | SpaceTabNode;

type SpaceMapping = Record<number, Space>;

type SpaceTabMapping = Record<number, SpaceTab>;

interface DataAsHashMap {
  spaces: SpaceMapping;
  spaceTabs: SpaceTabMapping;
  spaceOrder: SpaceOrder;
}

interface ParentNode {
  spaceId: number;
  private: boolean;
}

interface UpdatedReturn {
  updated: DataAsHashMap;
  prevParent: ParentNode;
}

interface ParentNodeProp {
  spaceId: number;
  private: boolean;
}

interface Parent {
  space: ParentNodeProp;
}

interface UpdateActionsProps {
  insertAt: number;
  prevParent: ParentNodeProp;
  parent: Parent;
  tabId: number;
}

interface SpaceActionsOrder {
  shared: Array<number>;
  private: Array<number>;
}

interface UpdateSpaceActionsProps {
  projectId: ProjectId;
  spaceOrder: SpaceActionsOrder;
}

interface RearrangeTreeProps {
  currentProjectId: ProjectId;
  updateActions: (e: UpdateActionsProps) => void;
  updateSpaceActions: (e: UpdateSpaceActionsProps) => void;
}

export const RearrangeTree: FC<RearrangeTreeProps> = ({
  currentProjectId,
  updateActions,
  updateSpaceActions,
}) => {
  const ref = useRef<any>(null);
  const windowHeight = window.innerHeight;
  const modalFooterHeight = 104;
  const modalHeaderHeight = 58;
  const treeHeight =
    windowHeight * 0.75 - modalFooterHeight - modalHeaderHeight;

  const { data: spaces = [] } = useProjectSpacesQuery(
    ProjectId(Number(currentProjectId)),
  );

  const [spaceTabId, setSpaceTabId] = useState(0);

  const [dataAsHashMap, setUpdatedHashMap] = useState(toHashMap(spaces));

  const privateSpacesTitle = useTranslation('projects.spaces.private');
  const sharedSpacesTitle = useTranslation('projects.spaces.shared');

  const spaceTypeTitle = useMemo(
    () => ({
      PrivateSpace: privateSpacesTitle,
      SharedSpace: sharedSpacesTitle,
    }),
    [privateSpacesTitle, sharedSpacesTitle],
  );

  const getSpaceTabs = useCallback((spaceTabs, level, isParentPrivate) => {
    return spaceTabs.map((spaceTab) => {
      return {
        ...spaceTab,
        title: spaceTab?.spaceTabTitle,
        type: RearrangeSpaceNodes.SpaceTabNode,
        key: `SPACE_TAB-${spaceTab.spaceTabId}`,
        level,
        className: `level-${level} ${isParentPrivate ? 'private' : ''}`,
      };
    });
  }, []);

  const getSpaces = useCallback(
    (spaceList, level, privacyType) => {
      return dataAsHashMap.spaceOrder[privacyType].map((spaceId) => {
        const space = spaceList.filter(
          (spaceItem) => spaceItem.spaceId === spaceId,
        )[0];

        return {
          ...space,
          title: space.spaceName,
          type: RearrangeSpaceNodes.SpaceNode,
          level,
          key: `SPACE-${space.spaceId}`,
          className: `space level-${level} ${space.private ? 'private' : ''}`,
          children: getSpaceTabs(space.spaceTabs, level + 1, space.private),
        };
      });
    },
    [dataAsHashMap.spaceOrder, getSpaceTabs],
  );

  const toTreeData = useCallback(
    (spaceData) => {
      return Object.keys(spaceData).map((spaceType) => {
        return {
          spaceType,
          title: spaceTypeTitle[spaceType],
          level: 0,
          type: RearrangeSpaceNodes.SpaceTypeNode,
          key: `SPACE_TYPE-${spaceTypeTitle[spaceType]}`,
          children: getSpaces(spaceData[spaceType], 1, spaceType),
          className: 'project-phase level-0',
          private: spaceType === 'PrivateSpace',
        };
      });
    },
    [spaceTypeTitle, getSpaces],
  );

  const dataAsHashMapValues: Array<Space> = Object.values(dataAsHashMap.spaces);
  const initialSpaceData: SpaceOrder = {
    PrivateSpace: [],
    SharedSpace: [],
  };
  const spaceData = dataAsHashMapValues.reduce((acc, curr: Space) => {
    if (curr.private) {
      return { ...acc, PrivateSpace: [...acc.PrivateSpace, curr] };
    }

    return { ...acc, SharedSpace: [...acc.SharedSpace, curr] };
  }, initialSpaceData);
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasAccessToPrivateSpaces = isAllowedForCurrentUser(
    PermissionRuleName.SpacePrivateSection,
  );
  const accessibleSpaceData = useMemo(
    () =>
      hasAccessToPrivateSpaces
        ? spaceData
        : {
            SharedSpace: spaceData.SharedSpace,
          },
    [hasAccessToPrivateSpaces, spaceData],
  );

  const treeData = useMemo(
    () => toTreeData(accessibleSpaceData),
    [toTreeData, accessibleSpaceData],
  );

  const documentCannotBeSpaceTitle = useTranslation(
    'projects.spaces.error.DocumentCannotBeSpace.title',
  );
  const documentCannotBeSpaceMessage = useTranslation(
    'projects.spaces.error.DocumentCannotBeSpace.message',
  );
  const spaceCannotBeSpaceTypeTitle = useTranslation(
    'projects.spaces.error.SpaceCannotBeSpaceType.title',
  );
  const spaceCannotBeSpaceTypeMessage = useTranslation(
    'projects.spaces.error.SpaceCannotBeSpaceType.message',
  );
  const spaceCannotBeSpaceTabTitle = useTranslation(
    'projects.spaces.error.SpaceCannotBeSpaceTab.title',
  );
  const spaceCannotBeSpaceTabMessage = useTranslation(
    'projects.spaces.error.SpaceCannotBeSpaceTab.message',
  );
  const privateSpaceCannotBeSharedTitle = useTranslation(
    'projects.spaces.error.PrivateSpaceCannotBeShared.title',
  );
  const privateSpaceCannotBeSharedMessage = useTranslation(
    'projects.spaces.error.PrivateSpaceCannotBeShared.message',
  );
  const sharedSpaceCannotBePrivateTitle = useTranslation(
    'projects.spaces.error.SharedSpaceCannotBePrivate.title',
  );
  const sharedSpaceCannotBePrivateMessage = useTranslation(
    'projects.spaces.error.SharedSpaceCannotBePrivate.message',
  );
  const documentMoved = useTranslation(
    'projects.spaces.notification.DocumentMovedTitle',
  );
  const documentMovedFromPrivateToSharedMessage = useTranslation(
    'projects.spaces.notification.DocumentMovedFromPrivateToSharedMessage',
  );
  const documentMovedFromSharedToPrivateMessage = useTranslation(
    'projects.spaces.notification.DocumentMovedFromSharedToPrivateMessage',
  );

  const showDocumentCannotBeSpaceMessage = useCallback(() => {
    toastService({
      title: documentCannotBeSpaceTitle,
      message: documentCannotBeSpaceMessage,
    }).error();
  }, [documentCannotBeSpaceMessage, documentCannotBeSpaceTitle]);

  const showSpaceCannotBeSpaceTypeMessage = useCallback(() => {
    toastService({
      title: spaceCannotBeSpaceTypeTitle,
      message: spaceCannotBeSpaceTypeMessage,
    }).error();
  }, [spaceCannotBeSpaceTypeMessage, spaceCannotBeSpaceTypeTitle]);

  const showSpaceCannotBeSpaceTabMessage = useCallback(() => {
    toastService({
      title: spaceCannotBeSpaceTabTitle,
      message: spaceCannotBeSpaceTabMessage,
    }).error();
  }, [spaceCannotBeSpaceTabMessage, spaceCannotBeSpaceTabTitle]);

  const showPrivateSpaceCannotBeShared = useCallback(() => {
    toastService({
      title: privateSpaceCannotBeSharedTitle,
      message: privateSpaceCannotBeSharedMessage,
    }).error();
  }, [privateSpaceCannotBeSharedMessage, privateSpaceCannotBeSharedTitle]);

  const showSharedSpaceCannotBePrivate = useCallback(() => {
    toastService({
      title: sharedSpaceCannotBePrivateTitle,
      message: sharedSpaceCannotBePrivateMessage,
    }).error();
  }, [sharedSpaceCannotBePrivateMessage, sharedSpaceCannotBePrivateTitle]);

  const showPrivateSpaceMovedIntoShared = useCallback(
    (docName) => {
      toastService({
        title: documentMoved,
        message: `${docName} ${documentMovedFromPrivateToSharedMessage}`,
      }).success();
    },
    [documentMoved, documentMovedFromPrivateToSharedMessage],
  );

  const showSharedSpaceMovedIntoPrivate = useCallback(
    (docName) => {
      toastService({
        title: documentMoved,
        message: `${docName} ${documentMovedFromSharedToPrivateMessage}`,
      }).success();
    },
    [documentMoved, documentMovedFromSharedToPrivateMessage],
  );

  const removeDocFromSpace = useCallback((data, spaceId, spaceTabID) => {
    const docPath = `spaces.${spaceId}.spaceTabs`;
    const spaceTabs = dotPropImmutable.get(data, docPath, []);

    return {
      updated: dotPropImmutable.set(
        data,
        docPath,
        spaceTabs.filter((spaceTab) => spaceTab.spaceTabId !== spaceTabID),
      ),
      prevParent: { spaceId, private: data.spaces[spaceId].private },
    };
  }, []);

  const removeDocFromParent = useCallback(
    (data, item): UpdatedReturn => {
      return removeDocFromSpace(data, item.space.spaceId, item.spaceTabId);
    },
    [removeDocFromSpace],
  );

  const insertDocIntoSpace = (data, newParent, docId, insertAt) => {
    const { spaceId } = newParent;
    const docPath = `spaces.${spaceId}.spaceTabs`;
    const spaceTabs = dotPropImmutable.get(data, docPath, []);

    const updated = dotPropImmutable.set(
      data,
      docPath,
      insert(spaceTabs, insertAt, {
        ...data.spaceTabs[docId],
        space: { spaceId },
      }),
    );

    return {
      data: updated,
      parent: {
        space: { spaceId: newParent.spaceId, private: newParent.private },
      },
    };
  };

  const getSiblings = (data, document) => {
    if (document?.space?.spaceId) {
      return dotPropImmutable.get(
        data,
        `spaces.${document.space.spaceId}.spaceTabs`,
      );
    }

    return [];
  };

  const getSpace = (data, document) => {
    if (document?.spaceId) {
      return dotPropImmutable.get(data, `spaces.${document.spaceId}`);
    }
    if (document?.space?.spaceId) {
      return dotPropImmutable.get(data, `spaces.${document.space.spaceId}`);
    }

    return '';
  };

  const allowDrop = useCallback(({ dropPosition }) => {
    if (dropPosition > 1) {
      return false;
    }

    return true;
  }, []);

  const onDrop = useCallback(
    ({ node: dropNode, dragNode }) => {
      const draggedTask = dragNode;
      const droppedOn = dropNode;

      if (draggedTask?.spaceId) {
        setSpaceTabId(draggedTask.spaceId);
      }
      if (draggedTask?.space?.spaceId) {
        setSpaceTabId(draggedTask.space.spaceId);
      }

      if (draggedTask.private === false && droppedOn.private === true) {
        showSharedSpaceCannotBePrivate();

        return;
      }

      try {
        if (draggedTask.type === 'SPACE' && droppedOn.type === 'SPACE-TYPE') {
          if (draggedTask.private) {
            let privateSpaceOrder = dataAsHashMap.spaceOrder.PrivateSpace;

            const insertAt = 0;

            privateSpaceOrder = privateSpaceOrder.filter(
              (spaceId) => spaceId !== draggedTask.spaceId,
            );
            privateSpaceOrder.splice(insertAt, 0, draggedTask.spaceId);

            const updatedSpaceOrder = {
              ...dataAsHashMap.spaceOrder,
              PrivateSpace: privateSpaceOrder,
            };

            const { PrivateSpace, SharedSpace } = updatedSpaceOrder;

            setUpdatedHashMap({
              ...dataAsHashMap,
              spaceOrder: updatedSpaceOrder,
            });
            updateSpaceActions({
              projectId: currentProjectId,
              spaceOrder: {
                private: PrivateSpace,
                shared: SharedSpace,
              },
            });

            return;
          }
          let sharedSpaceOrder = dataAsHashMap.spaceOrder.SharedSpace;

          const insertAt = 0;

          sharedSpaceOrder = sharedSpaceOrder.filter(
            (spaceId) => spaceId !== draggedTask.spaceId,
          );
          sharedSpaceOrder.splice(insertAt, 0, draggedTask.spaceId);

          const updatedSpaceOrder = {
            ...dataAsHashMap.spaceOrder,
            SharedSpace: sharedSpaceOrder,
          };

          const { PrivateSpace, SharedSpace } = updatedSpaceOrder;

          setUpdatedHashMap({
            ...dataAsHashMap,
            spaceOrder: updatedSpaceOrder,
          });
          updateSpaceActions({
            projectId: currentProjectId,
            spaceOrder: {
              private: PrivateSpace,
              shared: SharedSpace,
            },
          });

          return;
        }

        if (draggedTask.type === 'SPACE' && droppedOn.type === 'SPACE') {
          if (draggedTask.private) {
            let privateSpaceOrder = dataAsHashMap.spaceOrder.PrivateSpace;

            const insertAt = privateSpaceOrder.indexOf(droppedOn.spaceId) + 1;

            privateSpaceOrder = privateSpaceOrder.filter(
              (spaceId) => spaceId !== draggedTask.spaceId,
            );
            privateSpaceOrder.splice(insertAt, 0, draggedTask.spaceId);

            const updatedSpaceOrder = {
              ...dataAsHashMap.spaceOrder,
              PrivateSpace: privateSpaceOrder,
            };

            const { PrivateSpace, SharedSpace } = updatedSpaceOrder;

            setUpdatedHashMap({
              ...dataAsHashMap,
              spaceOrder: updatedSpaceOrder,
            });
            updateSpaceActions({
              projectId: currentProjectId,
              spaceOrder: {
                private: PrivateSpace,
                shared: SharedSpace,
              },
            });

            return;
          }
          let sharedSpaceOrder = dataAsHashMap.spaceOrder.SharedSpace;

          const insertAt = sharedSpaceOrder.indexOf(droppedOn.spaceId) + 1;

          sharedSpaceOrder = sharedSpaceOrder.filter(
            (spaceId) => spaceId !== draggedTask.spaceId,
          );
          sharedSpaceOrder.splice(insertAt, 0, draggedTask.spaceId);

          const updatedSpaceOrder = {
            ...dataAsHashMap.spaceOrder,
            SharedSpace: sharedSpaceOrder,
          };

          const { PrivateSpace, SharedSpace } = updatedSpaceOrder;

          setUpdatedHashMap({
            ...dataAsHashMap,
            spaceOrder: updatedSpaceOrder,
          });
          updateSpaceActions({
            projectId: currentProjectId,
            spaceOrder: {
              private: PrivateSpace,
              shared: SharedSpace,
            },
          });

          return;
        }
        if (dropNode.dragOver) {
          if (draggedTask.type === 'SPACE' && droppedOn.level === 0) {
            if (
              draggedTask.private === true &&
              droppedOn.spaceType === 'SharedSpace'
            ) {
              showPrivateSpaceCannotBeShared();

              return;
            }
          }

          if (draggedTask.type === 'SPACE' && droppedOn.level === 1) {
            if (draggedTask.private === true && droppedOn.private === false) {
              showPrivateSpaceCannotBeShared();

              return;
            }

            showSpaceCannotBeSpaceTabMessage();

            return;
          }

          if (draggedTask.type === 'SPACE-TAB' && droppedOn.level === 0) {
            showDocumentCannotBeSpaceMessage();

            return;
          }
          if (draggedTask.type === 'SPACE' && droppedOn.type === 'SPACE-TYPE') {
            showSpaceCannotBeSpaceTypeMessage();

            return;
          }

          const { updated, prevParent } = removeDocFromParent(
            dataAsHashMap,
            draggedTask,
          );

          const siblingTabs = getSiblings(updated, droppedOn) || [];

          const insertAt = siblingTabs
            ? siblingTabs
                .map((spaceTab) => spaceTab.spaceTabId)
                .indexOf(droppedOn.spaceTabId) + 1
            : 0;

          const { data, parent } = insertDocIntoSpace(
            updated,
            getSpace(updated, droppedOn),
            draggedTask.spaceTabId,
            insertAt,
          );

          setUpdatedHashMap(data);
          updateActions({
            tabId: draggedTask.spaceTabId,
            parent,
            prevParent,
            insertAt,
          });

          if (prevParent.private === true && parent.space.private === false) {
            showPrivateSpaceMovedIntoShared(draggedTask.title);
          }

          if (prevParent.private === false && parent.space.private === true) {
            showSharedSpaceMovedIntoPrivate(draggedTask.title);
          }

          return;
        }

        if (dropNode.dragOverGapBottom) {
          // dropped below document, adding it as next sibling

          const { updated, prevParent } = removeDocFromParent(
            dataAsHashMap,
            draggedTask,
          );
          const siblingTabs = getSiblings(updated, droppedOn);

          const insertAt = siblingTabs
            ? siblingTabs
                .map((spaceTab) => spaceTab.spaceTabId)
                .indexOf(droppedOn.spaceTabId) + 1
            : 0;

          const { data, parent } = insertDocIntoSpace(
            updated,
            getSpace(updated, droppedOn),
            draggedTask.spaceTabId,
            insertAt,
          );

          setUpdatedHashMap(data);

          updateActions({
            tabId: draggedTask.spaceTabId,
            parent,
            prevParent,
            insertAt,
          });

          if (prevParent.private === true && parent.space.private === false) {
            showPrivateSpaceMovedIntoShared(draggedTask.title);
          }
          if (prevParent.private === false && parent.space.private === true) {
            showSharedSpaceMovedIntoPrivate(draggedTask.title);
          }
        }
      } catch (error) {
        logger.error('rearranging failed', { error });
      }
    },
    [
      showSharedSpaceCannotBePrivate,
      dataAsHashMap,
      updateSpaceActions,
      currentProjectId,
      removeDocFromParent,
      updateActions,
      showPrivateSpaceCannotBeShared,
      showSpaceCannotBeSpaceTabMessage,
      showDocumentCannotBeSpaceMessage,
      showSpaceCannotBeSpaceTypeMessage,
      showPrivateSpaceMovedIntoShared,
      showSharedSpaceMovedIntoPrivate,
    ],
  );

  const [initialized, setInitialized] = useState(false);
  const tabKey = `TAB-${spaceTabId}`;
  const [selectedKeys, setSelectedKeys] = useState<string[]>([tabKey]);
  const [isDragging, setIsDragging] = useState(false);

  const getDepthOfTask = (type) => {
    if (type === 'SPACE') {
      return 1;
    }
    if (type === 'SPACE_TAB') {
      return 2;
    }

    return 0;
  };

  const treeClass = useMemo(() => {
    let type;

    if (selectedKeys[0].includes('SPACE_TAB')) {
      type = 'SPACE_TAB';
    } else if (selectedKeys[0].includes('SPACE')) {
      type = 'SPACE';
    }

    const draggedTaskDepth = getDepthOfTask(type);

    return `dragged-task-depth-${draggedTaskDepth} ${
      isDragging ? 'dragging' : ''
    }`;
  }, [isDragging, selectedKeys]);

  useEffect(() => {
    if (initialized) {
      return;
    }
    ref.current?.scrollTo({ key: tabKey });
    setInitialized(true);
  }, [initialized, ref, tabKey]);

  return (
    <>
      <Tree
        errorMessage={{
          dropOnNestedLevel: 'Space cannot be spaceTab ',
        }}
        ref={ref}
        treeData={treeData}
        className={treeClass}
        defaultExpandAll
        blockNode
        height={treeHeight}
        onDragStart={({ event, node }) => {
          if (
            (node as unknown as SpaceData).type ===
            RearrangeSpaceNodes.SpaceTypeNode
          ) {
            event.preventDefault();

            return;
          }
          setIsDragging(true);
          setSelectedKeys([node.key.toString()]);
        }}
        onDragEnd={() => setIsDragging(false)}
        onDragOver={({ node: { key } }) => {
          ref.current?.scrollTo({ key, offset: 10 });
        }}
        showIcon
        icon={(props) =>
          (props as unknown as SpaceData).type ===
            RearrangeSpaceNodes.SpaceTabNode ||
          (props as unknown as SpaceData).type ===
            RearrangeSpaceNodes.SpaceNode ? (
            <Draggable16 />
          ) : null
        }
        switcherIcon={<IconShell icon={<CaretDown16 />} />}
        allowDrop={allowDrop}
        onDrop={onDrop}
        draggable
        selectedKeys={selectedKeys}
      />
    </>
  );
};
